// ============================================================================
// Grandeurs et unités (moteur v4) : la feuille de brouillon des conversions.
// ============================================================================
// « 3,5 m³ = 3500 dm³ = 3500 L », « 2,5 h = 150 min », « 4 m × 2,5 m = 10 m² »,
// « 120 km ÷ 1,5 h = 80 km/h ». Une ligne est une chaîne de membres reliés par
// « = » ; chaque membre est une expression de GRANDEURS (un nombre suivi d'une
// unité, plusieurs grandeurs accolées s'ajoutent : « 2 h 30 min »).
//
// Le juge convertit tout en unités de base (m, g, s, €) et vérifie, maillon
// par maillon, que les deux membres ont la MÊME NATURE (longueur, aire,
// volume, masse, durée, vitesse…) et la même valeur. Une ligne doit partir de
// la question : au moins un membre est la grandeur de départ, un de ses
// morceaux, ou un membre déjà écrit. Le but : la grandeur de départ écrite
// dans l'unité demandée.
//
// Dans ce mode il n'y a PAS de lettre inconnue : toutes les lettres sont des
// unités. Les bouts d'unité de la palette portent une espace fine en tête
// (`UNIT_MARK`) : « 3,5 » puis « m³ » se lisent « 3,5 m³ ».
// Ce mode est séparé du moteur des égalités : il ne change rien aux autres feuilles.
// ============================================================================

import { formatNumber } from './lexer';
import type { Chip } from './lexer';
import type { Line, Node } from './tree';

export const UNIT_MARK = ' ';

/** dimensions : longueur, masse, durée, monnaie */
type Dim = [number, number, number, number];

interface UnitDef {
  f: number;
  d: Dim;
}

const L1: Dim = [1, 0, 0, 0];
const L2: Dim = [2, 0, 0, 0];
const L3: Dim = [3, 0, 0, 0];
const M1: Dim = [0, 1, 0, 0];
const T1: Dim = [0, 0, 1, 0];
const E1: Dim = [0, 0, 0, 1];

/** unités élémentaires (sans exposant) : facteur vers l'unité de base */
const UNITS: Record<string, UnitDef> = {
  km: { f: 1e3, d: L1 },
  hm: { f: 1e2, d: L1 },
  dam: { f: 10, d: L1 },
  m: { f: 1, d: L1 },
  dm: { f: 0.1, d: L1 },
  cm: { f: 0.01, d: L1 },
  mm: { f: 0.001, d: L1 },
  t: { f: 1e6, d: M1 },
  kg: { f: 1e3, d: M1 },
  hg: { f: 100, d: M1 },
  dag: { f: 10, d: M1 },
  g: { f: 1, d: M1 },
  dg: { f: 0.1, d: M1 },
  cg: { f: 0.01, d: M1 },
  mg: { f: 0.001, d: M1 },
  hL: { f: 0.1, d: L3 },
  daL: { f: 0.01, d: L3 },
  L: { f: 1e-3, d: L3 },
  dL: { f: 1e-4, d: L3 },
  cL: { f: 1e-5, d: L3 },
  mL: { f: 1e-6, d: L3 },
  ha: { f: 1e4, d: L2 },
  a: { f: 100, d: L2 },
  j: { f: 86400, d: T1 },
  h: { f: 3600, d: T1 },
  min: { f: 60, d: T1 },
  s: { f: 1, d: T1 },
  '€': { f: 1, d: E1 },
};
const NAMES = Object.keys(UNITS).sort((a, b) => b.length - a.length);

const SUPS: Record<string, number> = { '²': 2, '³': 3 };

/** une unité (éventuellement composée) : « m³ », « km/h », « g/cm³ » */
export interface Unit {
  text: string;
  f: number;
  d: Dim;
}

function powUnit(u: UnitDef, e: number): UnitDef {
  return { f: u.f ** e, d: u.d.map((x) => x * e) as Dim };
}
function mulDim(a: Dim, b: Dim, s = 1): Dim {
  return a.map((x, i) => x + s * b[i]!) as Dim;
}
function sameDim(a: Dim, b: Dim): boolean {
  return a.every((x, i) => x === b[i]);
}
const ZERO: Dim = [0, 0, 0, 0];

/** lit une unité au début de `s` (après d'éventuelles espaces) : { unit, len } */
function readUnitAt(s: string, i: number): { unit: Unit; len: number } | null {
  let j = i;
  const part = (): { def: UnitDef; text: string } | null => {
    const name = NAMES.find((n) => s.startsWith(n, j));
    if (!name) return null;
    // une unité n'est pas suivie d'une autre lettre (« min » n'est pas « m » + « in »)
    const after = s[j + name.length];
    if (after && /[A-Za-zÀ-ÿ]/.test(after)) return null;
    j += name.length;
    let def = UNITS[name]!;
    let text = name;
    let k = j;
    while (s[k] === ' ') k++;
    if (s[k] && SUPS[s[k]!]) j = k;
    const sup = s[j];
    if (sup && SUPS[sup]) {
      def = powUnit(def, SUPS[sup]!);
      text += sup;
      j++;
    }
    return { def, text };
  };
  const a = part();
  if (!a) return null;
  let f = a.def.f;
  let d = a.def.d;
  let text = a.text;
  for (;;) {
    let k = j;
    while (s[k] === ' ') k++;
    // « km/h » ; « km ÷ h » (une unité, pas un nombre, après la barre) se lit pareil
    if (s[k] !== '/' && s[k] !== '÷') break;
    const save = j;
    j = k + 1;
    while (s[j] === ' ') j++;
    const b = part();
    if (!b) {
      j = save;
      break;
    }
    f /= b.def.f;
    d = mulDim(d, b.def.d, -1);
    text += '/' + b.text;
  }
  return { unit: { text, f, d }, len: j - i };
}

/** Une unité connue, lue en entier (« km/h », « m³ ») ; null sinon. */
export function parseUnit(text: string): Unit | null {
  const t = text.replace(UNIT_MARK, '').trim();
  const r = readUnitAt(t, 0);
  return r && r.len === t.length ? r.unit : null;
}

/** Atome d'unité (bout de palette) : « m³ » précédé de l'espace fine. */
export function unitAtom(text: string): string {
  return UNIT_MARK + text;
}
export function isUnitAtom(a: string | undefined): boolean {
  return !!a && a.length > 1 && a[0] === UNIT_MARK;
}

// ── lecture d'une ligne ────────────────────────────────────────────────────

type Tok = { t: 'num'; v: number; text: string } | { t: 'unit'; u: Unit } | { t: 'op'; o: string };

export class UnitsSyntaxError extends Error {}

function tokenize(text: string): Tok[] {
  const s = text.replace(/\u2009|\u202F|\u00A0/g, ' ');
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    const num = /^\d+(?:[,.]\d+)?/.exec(s.slice(i));
    if (num) {
      out.push({ t: 'num', v: Number(num[0].replace(',', '.')), text: num[0].replace('.', ',') });
      i += num[0].length;
      continue;
    }
    if ('+−-×÷()=/'.includes(c) || c === '*') {
      const o = c === '-' ? '−' : c === '*' ? '×' : c === '/' ? '÷' : c;
      out.push({ t: 'op', o });
      i++;
      continue;
    }
    const u = readUnitAt(s, i);
    if (u) {
      out.push({ t: 'unit', u: u.unit });
      i += u.len;
      continue;
    }
    throw new UnitsSyntaxError(`« ${s.slice(i).split(/\s/)[0]} » n’est pas une unité connue.`);
  }
  return out;
}

/** valeur d'un membre : nombre en unités de base + dimension */
export interface QVal {
  v: number;
  d: Dim;
}

/** arbre d'un membre */
export type QNode =
  | { k: 'q'; parts: { v: number; text: string; u: Unit | null }[] }
  | { k: 'add'; a: QNode; b: QNode; op: '+' | '−' }
  | { k: 'mul'; a: QNode; b: QNode; op: '×' | '÷' }
  | { k: 'neg'; a: QNode }
  | { k: 'par'; a: QNode };

function parseSide(toks: Tok[]): QNode {
  let p = 0;
  const peek = () => toks[p];
  const expr = (): QNode => {
    let a = term();
    for (;;) {
      const t = peek();
      if (t && t.t === 'op' && (t.o === '+' || t.o === '−')) {
        p++;
        a = { k: 'add', a, b: term(), op: t.o };
      } else return a;
    }
  };
  const term = (): QNode => {
    let a = factor();
    for (;;) {
      const t = peek();
      if (t && t.t === 'op' && (t.o === '×' || t.o === '÷')) {
        p++;
        a = { k: 'mul', a, b: factor(), op: t.o };
      } else return a;
    }
  };
  const factor = (): QNode => {
    const t = peek();
    if (!t) throw new UnitsSyntaxError('La ligne s’arrête au milieu d’un calcul.');
    if (t.t === 'op' && t.o === '−') {
      p++;
      return { k: 'neg', a: factor() };
    }
    if (t.t === 'op' && t.o === '(') {
      p++;
      const a = expr();
      const c = peek();
      if (!c || c.t !== 'op' || c.o !== ')')
        throw new UnitsSyntaxError('Une parenthèse n’est pas refermée.');
      p++;
      return { k: 'par', a };
    }
    if (t.t === 'unit')
      throw new UnitsSyntaxError(`Écris un nombre avant l’unité « ${t.u.text} ».`);
    if (t.t !== 'num') throw new UnitsSyntaxError(`« ${t.o} » arrive trop tôt.`);
    // grandeurs accolées : « 2 h 30 min »
    const parts: { v: number; text: string; u: Unit | null }[] = [];
    while (peek() && peek()!.t === 'num') {
      const n = toks[p++] as { t: 'num'; v: number; text: string };
      const u = peek();
      if (u && u.t === 'unit') {
        p++;
        parts.push({ v: n.v, text: n.text, u: u.u });
      } else {
        parts.push({ v: n.v, text: n.text, u: null });
        break;
      }
    }
    if (parts.length > 1 && parts.some((x) => !x.u))
      throw new UnitsSyntaxError('Deux nombres se suivent : mets une opération entre eux.');
    return { k: 'q', parts };
  };
  const out = expr();
  if (p < toks.length) {
    const t = toks[p]!;
    throw new UnitsSyntaxError(
      t.t === 'unit'
        ? `L’unité « ${t.u.text} » suit déjà une unité.`
        : t.t === 'num'
          ? 'Deux nombres se suivent : mets une opération entre eux.'
          : `« ${t.o} » est mal placé.`,
    );
  }
  return out;
}

export class UnitsDimError extends Error {}

const NATURE: [Dim, string][] = [
  [L1, 'une longueur'],
  [L2, 'une aire'],
  [L3, 'un volume'],
  [M1, 'une masse'],
  [T1, 'une durée'],
  [E1, 'un prix'],
  [[1, 0, -1, 0], 'une vitesse'],
  [[-3, 1, 0, 0], 'une masse volumique'],
  [[3, 0, -1, 0], 'un débit'],
  [ZERO, 'un nombre sans unité'],
];
export function nature(d: Dim): string {
  const n = NATURE.find(([x]) => sameDim(x, d));
  return n ? n[1] : 'une grandeur composée';
}

export function qval(t: QNode): QVal {
  switch (t.k) {
    case 'q': {
      let v = 0;
      let d: Dim | null = null;
      for (const x of t.parts) {
        const dd = x.u ? x.u.d : ZERO;
        if (d && !sameDim(d, dd))
          throw new UnitsDimError('On n’accole que des grandeurs de même nature (« 2 h 30 min »).');
        d = dd;
        v += x.v * (x.u ? x.u.f : 1);
      }
      return { v, d: d ?? ZERO };
    }
    case 'par':
      return qval(t.a);
    case 'neg': {
      const a = qval(t.a);
      return { v: -a.v, d: a.d };
    }
    case 'add': {
      const a = qval(t.a);
      const b = qval(t.b);
      if (!sameDim(a.d, b.d))
        throw new UnitsDimError(
          `On ne peut pas ${t.op === '+' ? 'ajouter' : 'soustraire'} ${nature(a.d)} et ${nature(b.d)}.`,
        );
      return { v: t.op === '+' ? a.v + b.v : a.v - b.v, d: a.d };
    }
    case 'mul': {
      const a = qval(t.a);
      const b = qval(t.b);
      if (t.op === '÷' && b.v === 0) throw new UnitsDimError('On ne divise pas par 0.');
      return t.op === '×'
        ? { v: a.v * b.v, d: mulDim(a.d, b.d) }
        : { v: a.v / b.v, d: mulDim(a.d, b.d, -1) };
    }
  }
}

function qtext(t: QNode): string {
  switch (t.k) {
    case 'q':
      return t.parts.map((x) => (x.u ? `${x.text} ${x.u.text}` : x.text)).join(' ');
    case 'par':
      return '(' + qtext(t.a) + ')';
    case 'neg':
      return '−' + qtext(t.a);
    case 'add':
      return qtext(t.a) + ' ' + t.op + ' ' + qtext(t.b);
    case 'mul':
      return qtext(t.a) + ' ' + t.op + ' ' + qtext(t.b);
  }
}
/** clé d'un membre : son écriture, nombres recalculés (« 3,50 m » = « 3,5 m ») */
function qkey(t: QNode): string {
  switch (t.k) {
    case 'q':
      return t.parts.map((x) => formatNumber(x.v) + (x.u ? ' ' + x.u.text : '')).join(' ');
    case 'par':
      return qkey(t.a);
    case 'neg':
      return '−(' + qkey(t.a) + ')';
    case 'add':
      return '(' + qkey(t.a) + t.op + qkey(t.b) + ')';
    case 'mul': {
      if (t.op === '×') {
        const a = qkey(t.a);
        const b = qkey(t.b);
        return '(' + [a, b].sort().join('×') + ')';
      }
      return '(' + qkey(t.a) + '÷' + qkey(t.b) + ')';
    }
  }
}
/** les morceaux d'un membre (sous-expressions), lui compris */
function subKeys(t: QNode, out: Set<string>): Set<string> {
  out.add(qkey(t));
  if (t.k === 'par' || t.k === 'neg') subKeys(t.a, out);
  if (t.k === 'add' || t.k === 'mul') {
    subKeys(t.a, out);
    subKeys(t.b, out);
  }
  if (t.k === 'q' && t.parts.length === 1 && t.parts[0]!.u) out.add(qkey(t));
  return out;
}

/** une seule grandeur, sans calcul : « 3500 dm³ », « 2 h 30 min » */
function singleUnits(t: QNode): string | null {
  const u = t.k === 'par' ? t.a : t;
  if (u.k !== 'q' || u.parts.some((x) => !x.u)) return null;
  return u.parts.map((x) => x.u!.text).join(' ');
}

export interface UnitsSpec {
  target: string;
  unit: string;
  valueUnit?: string;
  path: string[];
}

export interface UnitsLine {
  sides: QNode[];
}

export function parseUnitsText(text: string): UnitsLine {
  const toks = tokenize(text);
  if (toks.some((t) => t.t === 'op' && t.o === ('≡' as string)))
    throw new UnitsSyntaxError('Ici on écrit des égalités de grandeurs.');
  const sides: Tok[][] = [[]];
  toks.forEach((t) => {
    if (t.t === 'op' && t.o === '=') sides.push([]);
    else sides[sides.length - 1]!.push(t);
  });
  if (sides.some((s) => !s.length)) throw new UnitsSyntaxError('Un membre de l’égalité est vide.');
  return { sides: sides.map(parseSide) };
}

/** la ligne au format du moteur (membres opaques, pour l'affichage) */
export function unitsLineToLine(u: UnitsLine): Line {
  const sides: Node[] = u.sides.map((s) => ({ k: 's', n: qtext(s) }));
  return { dis: [{ sides }] };
}
function lineToUnits(L: Line): UnitsLine {
  return parseUnitsText(L.dis[0]!.sides.map((s) => s.n ?? '').join(' = '));
}

// ── état ───────────────────────────────────────────────────────────────────

export interface UnitsState {
  spec: UnitsSpec;
  target: QNode;
  tv: QVal;
  /** unité(s) demandée(s), écrites comme dans la réponse : « L », « h min » */
  unit: string;
  valueUnit: Unit;
  /** écritures reliées à la question (membres établis et leurs morceaux) */
  linked: Set<string>;
  /** membres écrits (clé → valeur) */
  sides: Map<string, { node: QNode; val: QVal }>;
  /** valeurs en jeu (la cible, les membres écrits et leurs morceaux) */
  vals: QVal[];
}

const close = (a: number, b: number) =>
  Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));

export function createUnitsState(spec: UnitsSpec, givens: string[] = []): UnitsState {
  const target = parseUnitsText(spec.target).sides[0]!;
  const tv = qval(target);
  const unit = spec.unit
    .split(/\s+/)
    .map((u) => parseUnit(u)!.text)
    .join(' ');
  const vu = parseUnit(spec.valueUnit ?? spec.unit.split(/\s+/).pop()!);
  if (!vu) throw new UnitsSyntaxError(`Unité inconnue : ${spec.unit}`);
  const us: UnitsState = {
    spec,
    target,
    tv,
    unit,
    valueUnit: vu,
    linked: subKeys(target, new Set()),
    sides: new Map(),
    vals: [],
  };
  addVals(us, target);
  us.sides.set(qkey(target), { node: target, val: tv });
  givens.forEach((g) => {
    try {
      addUnitsFact(us, parseUnitsText(g));
    } catch {
      /* donnée illisible : ignorée */
    }
  });
  return us;
}

function addVals(us: UnitsState, t: QNode): void {
  try {
    us.vals.push(qval(t));
  } catch {
    /* morceau sans valeur */
  }
  if (t.k === 'par' || t.k === 'neg') addVals(us, t.a);
  if (t.k === 'add' || t.k === 'mul') {
    addVals(us, t.a);
    addVals(us, t.b);
  }
}
/** le membre parle-t-il de la question ? (même écriture, ou même valeur, qu'un morceau en jeu) */
function isLinked(us: UnitsState, s: QNode): boolean {
  if (us.linked.has(qkey(s)) || us.sides.has(qkey(s))) return true;
  let v: QVal;
  try {
    v = qval(s);
  } catch {
    return false;
  }
  return us.vals.some((w) => sameDim(w.d, v.d) && close(w.v, v.v));
}

function addUnitsFact(us: UnitsState, u: UnitsLine): number {
  let fresh = 0;
  u.sides.forEach((s) => {
    const k = qkey(s);
    if (!us.sides.has(k)) {
      fresh++;
      us.sides.set(k, { node: s, val: qval(s) });
    }
    subKeys(s, us.linked);
    addVals(us, s);
  });
  return fresh;
}

export function addUnitsLine(us: UnitsState, L: Line): void {
  addUnitsFact(us, lineToUnits(L));
}

function isAnswerSide(us: UnitsState, s: QNode): boolean {
  if (singleUnits(s) !== us.unit) return false;
  const v = qval(s);
  return sameDim(v.d, us.tv.d) && close(v.v, us.tv.v);
}

export function unitsGoalReached(us: UnitsState): boolean {
  for (const { node } of us.sides.values()) if (isAnswerSide(us, node)) return true;
  return false;
}

/** valeurs trouvées pour la grandeur cherchée, exprimées dans `valueUnit` */
export function unitsStatedValues(us: UnitsState): number[] {
  const out: number[] = [];
  for (const { node, val } of us.sides.values())
    if (isAnswerSide(us, node)) out.push(val.v / us.valueUnit.f);
  return out;
}

function lineTrue(u: UnitsLine): boolean {
  try {
    const vals = u.sides.map(qval);
    return vals.every((v, i) => !i || (sameDim(v.d, vals[0]!.d) && close(v.v, vals[0]!.v)));
  } catch {
    return false;
  }
}
export function unitsIsTrue(L: Line): boolean {
  try {
    return lineTrue(lineToUnits(L));
  } catch {
    return false;
  }
}
export function unitsEstablished(us: UnitsState, L: Line): boolean {
  try {
    return lineToUnits(L).sides.every((s) => us.sides.has(qkey(s)));
  } catch {
    return false;
  }
}
/** un texte de réponse (« 3500 L ») est-il la grandeur cherchée ? */
export function unitsIsAnswer(us: UnitsState, text: string): boolean {
  try {
    const u = parseUnitsText(text);
    return u.sides.every((s) => isAnswerSide(us, s));
  } catch {
    return false;
  }
}

// ── juge ───────────────────────────────────────────────────────────────────

export interface UnitsVerdict {
  kind:
    | 'empty'
    | 'syntax'
    | 'trap'
    | 'open'
    | 'pending'
    | 'step'
    | 'false'
    | 'unjustified'
    | 'useless';
  atoms: string[];
  title?: string;
  message?: string;
  badChip?: number;
  complete?: boolean;
  line?: Line;
  hora?: number;
  final?: boolean;
  rule?: { id: string; card: string; label: string; hora: number; why: string };
  trap?: { line: Line; title: string; why: string };
}

/** le texte d'une ligne posée avec des bouts */
export function chipsText(chips: Chip[]): string {
  return chips
    .flatMap((c) => c.atoms)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function pow10(r: number): number | null {
  if (r <= 0) return null;
  const k = Math.round(Math.log10(r));
  return k !== 0 && close(r, 10 ** k) ? k : null;
}

function wrongValue(a: QNode, b: QNode, va: QVal, vb: QVal): string {
  const sa = singleUnits(a);
  const sb = singleUnits(b);
  const base = `« ${qtext(a)} = ${qtext(b)} » est faux.`;
  if (sa && sb && sa !== sb) {
    const ua = parseUnit(sa.split(' ').pop()!);
    const ub = parseUnit(sb.split(' ').pop()!);
    // durées : une heure fait 60 minutes, pas 100
    if (
      sameDim(va.d, T1) &&
      (sa.includes('h') || sb.includes('h')) &&
      (sa.includes('min') || sb.includes('min'))
    )
      return `${base} Une heure fait 60 minutes (pas 100) : 0,5 h = 30 min, 0,25 h = 15 min.`;
    if (ua && ub && sa.split(' ').length === 1 && sb.split(' ').length === 1) {
      const factor = ub.f / ua.f; // 1 ub = factor ua
      const k = pow10(vb.v / va.v);
      const rel =
        factor >= 1
          ? `1 ${ub.text} = ${formatNumber(factor)} ${ua.text}`
          : `1 ${ua.text} = ${formatNumber(1 / factor)} ${ub.text}`;
      return (
        base +
        (k !== null
          ? ` Ton nombre est ${formatNumber(10 ** Math.abs(k))} fois trop ${k > 0 ? 'grand' : 'petit'}. `
          : ' ') +
        `Repère : ${rel}.`
      );
    }
  }
  return base + ' Calcule à nouveau, en gardant les unités.';
}

export function unitsJudge(
  us: UnitsState,
  traps: { line: string; title: string; why: string }[],
  chips: Chip[],
  opts?: { verify?: boolean },
): UnitsVerdict {
  const A = chips.flatMap((c) => c.atoms);
  if (!A.length) return { kind: 'empty', atoms: A, badChip: -1 };
  const text = chipsText(chips);
  if (!text.includes('=')) {
    try {
      tokenize(text);
    } catch (e) {
      return {
        kind: 'syntax',
        atoms: A,
        title: 'Ça commence mal',
        message: (e as Error).message,
        badChip: chips.length - 1,
      };
    }
    return { kind: 'open', atoms: A, complete: false };
  }
  let u: UnitsLine;
  try {
    u = parseUnitsText(text);
  } catch (e) {
    // une ligne en cours (« 3,5 m³ = », « 2 h 45 ») reste ouverte
    const unclosed = (text.match(/\(/g) ?? []).length > (text.match(/\)/g) ?? []).length;
    if (unclosed || /[=+−×÷(/]\s*$/.test(text) || /\d\s*$/.test(text))
      return { kind: 'open', atoms: A, complete: false };
    return {
      kind: 'syntax',
      atoms: A,
      title: 'Ça commence mal',
      message: (e as Error).message,
      badChip: chips.length - 1,
    };
  }
  const v = evaluate(us, traps, u, A);
  if (!opts?.verify && (v.kind === 'unjustified' || v.kind === 'false'))
    return { kind: 'pending', atoms: A, complete: true };
  return v;
}

function evaluate(
  us: UnitsState,
  traps: { line: string; title: string; why: string }[],
  u: UnitsLine,
  A: string[],
): UnitsVerdict {
  const line = unitsLineToLine(u);
  const keyOf = (x: UnitsLine) => x.sides.map(qkey).join(' = ');
  const k = keyOf(u);
  const trap = traps.find((t) => {
    try {
      return keyOf(parseUnitsText(t.line)) === k;
    } catch {
      return false;
    }
  });
  let vals: QVal[];
  try {
    vals = u.sides.map(qval);
  } catch (e) {
    return { kind: 'false', atoms: A, title: 'Pas la même nature', message: (e as Error).message };
  }
  for (let i = 0; i + 1 < vals.length; i++) {
    const a = vals[i]!;
    const b = vals[i + 1]!;
    const ok = sameDim(a.d, b.d) && close(a.v, b.v);
    if (ok) continue;
    if (trap)
      return {
        kind: 'trap',
        atoms: A,
        title: 'Piège : ' + trap.title.charAt(0).toLowerCase() + trap.title.slice(1),
        message: trap.why,
        trap: { line, title: trap.title, why: trap.why },
        badChip: -1,
      };
    if (!sameDim(a.d, b.d))
      return {
        kind: 'false',
        atoms: A,
        title: 'Pas la même nature',
        message: `À gauche, ${nature(a.d)} ; à droite, ${nature(b.d)} : elles ne peuvent pas être égales. Vérifie tes unités${sameDim(a.d, L2) || sameDim(b.d, L2) || sameDim(a.d, L3) || sameDim(b.d, L3) ? ' (et les ² ou ³)' : ''}.`,
      };
    return {
      kind: 'false',
      atoms: A,
      title: 'Ce n’est pas égal',
      message: wrongValue(u.sides[i]!, u.sides[i + 1]!, a, b),
    };
  }
  // la ligne part de la question (ou d'un membre déjà écrit)
  const linked = u.sides.some((s) => isLinked(us, s));
  if (!linked)
    return {
      kind: 'useless',
      atoms: A,
      line,
      title: 'Juste, mais à côté de la question',
      message: `C’est vrai, mais ça ne part pas de ${qtext(us.target)}. Garde-la comme repère, ou pars de la grandeur de la question.`,
    };
  const fresh = u.sides.filter((s) => !us.sides.has(qkey(s))).length;
  if (!fresh)
    return {
      kind: 'useless',
      atoms: A,
      line,
      title: 'Juste, mais ça ne t’avance pas',
      message: 'Tu avais déjà écrit tout ça.',
    };
  const before = unitsGoalReached(us);
  const final = !before && u.sides.some((s) => isAnswerSide(us, s));
  const conv = u.sides.some((s, i) => {
    const a = singleUnits(s);
    return !!a && u.sides.some((t, j) => j !== i && singleUnits(t) && singleUnits(t) !== a);
  });
  const hora = Math.min(3, fresh);
  const label = conv ? 'Conversion' : 'Calcul avec les unités';
  return {
    kind: 'step',
    atoms: A,
    line,
    final,
    hora,
    rule: {
      id: conv ? 'conversion' : 'grandeurs',
      card: '',
      label,
      hora,
      why: final
        ? `${qtext(us.target)} est écrit en ${us.unit} : c’est la réponse.`
        : 'Chaque maillon est juste : même nature, même valeur.',
    },
  };
}

// ── corrigé ────────────────────────────────────────────────────────────────

export function unitsPathLines(spec: UnitsSpec): Line[] {
  return spec.path.map((p) => unitsLineToLine(parseUnitsText(p)));
}

/** les unités qui apparaissent dans un texte (pour la palette) */
export function unitsIn(text: string): string[] {
  try {
    return tokenize(text).flatMap((t) => (t.t === 'unit' ? [t.u.text] : []));
  } catch {
    return [];
  }
}
/** les nombres qui apparaissent dans un texte (pour la palette) */
export function numbersIn(text: string): string[] {
  try {
    return tokenize(text).flatMap((t) => (t.t === 'num' ? [formatNumber(t.v)] : []));
  } catch {
    return [];
  }
}
